import * as _ from 'lodash';
import * as React from 'react';
import {useState} from 'react';
import {Card, Loader} from 'semantic-ui-react';
import {runLink} from '../util/runhelpers';
import {useLoadFile} from '../util/requests';
import MessageMediaNotFound from './MessageMediaNotFound';
import {JSONparseUserFile} from '../util/jsonnan';
import {MediaCardProps} from './MediaCard';
import PanelError from './elements/PanelError';
import {labelComponent, useStepWithRollback} from '../util/media';
import makeComp from '../util/profiler';

function usePlotlyCardData(props: MediaCardProps) {
  const {runSignature, mediaKey} = props;
  const {currentStep, stepData, failedToLoad, rollback} = useStepWithRollback(
    props
  );

  // If this is file-based media, we put the contents of the file here once
  // loaded
  const [filePlotlyData, setFilePlotlyData] = useState();
  const [error, setError] = useState<string | undefined>();

  const stepMedia: null | {_type: string; path: string; plot: any} =
    stepData != null ? stepData[mediaKey] : null;

  const mediaFilePath: string | null =
    stepMedia != null && stepMedia._type === 'plotly-file'
      ? stepMedia.path
      : null;

  const onSuccess = React.useCallback(async (file: File) => {
    const fileContents = await new Response(file).text();
    const parsedPlot = JSONparseUserFile(fileContents);
    if (parsedPlot.error) {
      setError("Couldn't parse JSON data.");
    } else {
      setFilePlotlyData(parsedPlot.result);
    }
  }, []);

  // Use empty string when we have null and skip the request
  // ! Warning: The callbacks passed to useLoadFile must stay
  // equal by reference or it will retrigger a new file download.
  const loading = useLoadFile(runSignature, mediaFilePath || '', {
    responseType: 'file',
    onSuccess,
    fallback: rollback,
    skip: stepMedia?.plot != null || mediaFilePath == null || failedToLoad,
  });

  const plotlyData = filePlotlyData || stepMedia?.plot || null;

  return {
    plotlyData,
    loading,
    currentStep,
    error,
  };
}

// a single image card

const PlotlyCard: React.FunctionComponent<MediaCardProps> = makeComp(
  props => {
    const {run, mediaKey, mediaIndex, runSignature, width, height} = props;

    // Load plotly library
    type Plotly = typeof import('plotly.js');
    const [Plotly, setPlotly] = React.useState<Plotly>();
    React.useEffect(() => {
      import('plotly.js').then(setPlotly);
    }, []);

    // Debounce plotly's newPlot.
    const plotlyNewPlot = React.useCallback(
      Plotly != null
        ? _.debounce((...args: Parameters<typeof Plotly.react>) => {
            let err = false;
            try {
              // Changed from Plotly.newPlot to Plotly.react to fix disappearing plots due to webgl context limit
              // see https://github.com/plotly/plotly.js/issues/2333#issuecomment-391835536
              Plotly.react(...args);
            } catch (e) {
              err = true;
            }
            setPlotlyRenderError(err);
          }, 100)
        : () => {},
      [Plotly]
    );

    // Div to stick plotly chart into
    const plotlyDivRef = React.useRef<HTMLDivElement>(null);

    const [plotlyRenderError, setPlotlyRenderError] = useState(false);

    const {plotlyData, loading, currentStep, error} = usePlotlyCardData(props);

    // Use the plotly library to render the loaded plotly data to the div
    React.useEffect(() => {
      // Prevent ugly loading states
      if (width === 0) {
        return;
      }

      if (
        Plotly != null &&
        plotlyDivRef.current != null &&
        plotlyData != null
      ) {
        const copy = _.cloneDeep(plotlyData);

        if (copy.layout) {
          if ('width' in copy.layout) {
            delete copy.layout.width;
          }
          copy.layout.height = props.height + 14;
          copy.layout.autosize = true;
          // TODO: probably need better logic here...
          if (copy.layout.margin) {
            copy.layout.margin.t = 0;
            copy.layout.margin.l = 0;
            copy.layout.margin.r = 0;
            copy.layout.margin.b = 0;
            copy.layout.pad = 0;
          }
        }
        // Webgl is way faster when these are larger. Unfortunately
        // we also run out of GL contexts this way.
        if (_.isArray(copy.data)) {
          copy.data = copy.data.map((d: any) => ({
            ...d,
            type: d.type === 'scatter' ? 'scattergl' : d.type,
          }));
        }
        // TODO: Plotly.react will be faster but couldn't get datarevision to work
        // copy.layout.datarevision = this.state.currentStepIndex;
        plotlyNewPlot(plotlyDivRef.current, copy.data, copy.layout, {
          responsive: true,
        });
      }
    }, [props.height, plotlyData, Plotly, plotlyNewPlot, width]);

    const titleLink = runLink(runSignature, run.displayName, {
      className: 'hide-in-run-page',
      target: '_blank',
      rel: 'noopener noreferrer',
    });

    return (
      <Card className="plotly-card" style={{width: '100%'}}>
        {/* TITLE (Run Name or Description) */}
        {labelComponent(props, currentStep, titleLink)}
        <div ref={plotlyDivRef} />
        {plotlyData == null && loading ? (
          <div style={{width, height}}>
            <Loader active />
          </div>
        ) : plotlyRenderError ? (
          <PanelError
            message={
              <div>Plotly rendering error, try resizing this panel.</div>
            }
          />
        ) : (
          plotlyData == null &&
          (error != null ? (
            <PanelError
              className="media-missing"
              message={<div>{error}</div>}
            />
          ) : (
            <MessageMediaNotFound
              mediaIndex={mediaIndex}
              mediaKey={mediaKey}
              stepIndex={currentStep}
              mediaType="table"
            />
          ))
        )}
      </Card>
    );
  },
  {id: 'PlotlyCard'}
);

export default PlotlyCard;
